import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Sunrise, Sun, Moon, Cookie, Lightbulb, ShoppingBasket, CalendarRange, Download, BookmarkPlus, Trash2, Bookmark, Heart, Plus } from "lucide-react";
import { toast } from "sonner";

const MEALS = [
  { key: "breakfast", label: "Breakfast", Icon: Sunrise, accent: "amber" },
  { key: "lunch", label: "Lunch", Icon: Sun, accent: "emerald" },
  { key: "dinner", label: "Dinner", Icon: Moon, accent: "sky" },
];

export default function MealPlan() {
  const [usage, setUsage] = useState(null);
  const loadUsage = () => api.get("/meal-plan/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => { loadUsage(); }, []);

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="mb-6 fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Meal plans</h1>
          <p className="text-slate-500 mt-2">AI-built around your conditions, meds and food preferences.</p>
        </div>
        {usage && (
          <div className="text-right" data-testid="usage-badge">
            <div className="text-xs text-slate-400 uppercase tracking-wider">Today</div>
            <div className={`text-sm font-semibold ${usage.remaining === 0 ? "text-red-600" : "text-emerald-700"}`}>
              {usage.remaining} of {usage.limit} generations left
            </div>
            <div className="text-xs text-slate-500 mt-0.5" data-testid="usage-reset-time">Resets at 12AM GMT</div>
          </div>
        )}
      </div>
      <Tabs defaultValue="daily" className="fade-up delay-1">
        <TabsList className="bg-emerald-50/70 rounded-full p-1.5 mb-6 h-auto flex-wrap">
          <TabsTrigger value="daily" data-testid="tab-daily" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow px-5 py-2">
            <Sun className="w-4 h-4 mr-2" /> Today
          </TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow px-5 py-2">
            <CalendarRange className="w-4 h-4 mr-2" /> This week
          </TabsTrigger>
          <TabsTrigger value="grocery" data-testid="tab-grocery" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow px-5 py-2">
            <ShoppingBasket className="w-4 h-4 mr-2" /> Grocery list
          </TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow px-5 py-2">
            <Bookmark className="w-4 h-4 mr-2" /> Saved
          </TabsTrigger>
        </TabsList>
        <TabsContent value="daily"><DailyPlan usage={usage} onUsageChange={loadUsage} /></TabsContent>
        <TabsContent value="weekly"><WeeklyPlan /></TabsContent>
        <TabsContent value="grocery"><GroceryList /></TabsContent>
        <TabsContent value="saved"><SavedPlans /></TabsContent>
      </Tabs>
    </div>
  );
}

function downloadBlob(blob, headers, fallback = "meal-plan.pdf") {
  const disp = headers?.["content-disposition"] || "";
  const m = disp.match(/filename="?([^"]+)"?/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = m ? m[1] : fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function SavePlanDialog({ open, defaultName, onClose, onSave, busy }) {
  const [name, setName] = useState(defaultName || "");
  useEffect(() => { if (open) setName(defaultName || ""); }, [open, defaultName]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="rounded-2xl border-emerald-100 p-6 max-w-md w-full bg-white" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-semibold text-slate-900 mb-4">Save this meal plan</h3>
        <Input className="rounded-xl" value={name} onChange={(e) => setName(e.target.value)} placeholder="Give it a name…" data-testid="save-plan-name" autoFocus />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(name)} disabled={busy || !name.trim()} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-plan-confirm">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function SaveComponentDialog({ open, defaultName, onClose, onSave, busy }) {
  const [name, setName] = useState(defaultName || "");
  useEffect(() => { if (open) setName(defaultName || ""); }, [open, defaultName]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="rounded-2xl border-emerald-100 p-6 max-w-md w-full bg-white" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-semibold text-slate-900 mb-4">Save this meal to your library</h3>
        <Input className="rounded-xl" value={name} onChange={(e) => setName(e.target.value)} placeholder="Give it a name…" data-testid="save-component-name" autoFocus />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(name)} disabled={busy || !name.trim()} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-component-confirm">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function DailyPlan({ usage, onUsageChange }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [savingBusy, setSavingBusy] = useState(false);
  const [compOpen, setCompOpen] = useState(null);   // { component, snack_index, defaultName }
  const [compBusy, setCompBusy] = useState(false);

  useEffect(() => { api.get("/meal-plan").then((r) => setPlan(r.data || null)).catch(() => setError("Could not load your daily plan. Please try again.")); }, []);

  const overQuota = usage && usage.remaining === 0;

  const openSaveComponent = (component, snackIndex = null) => {
    const src = component === "snack" ? (plan?.snacks?.[snackIndex]) : plan?.[component];
    if (!src) return;
    setCompOpen({
      component,
      snack_index: snackIndex,
      defaultName: src.name || component,
    });
  };

  const saveComponent = async (name) => {
    if (!compOpen) return;
    setCompBusy(true);
    try {
      await api.post("/meal-plans/saved-component", {
        name: name.trim(),
        component: compOpen.component,
        snack_index: compOpen.snack_index,
      });
      toast.success("Saved to your meal library");
      setCompOpen(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setCompBusy(false); }
  };

  const generate = async () => {
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/meal-plan/generate");
      if (r.data.error) { setError(r.data.error); toast.error(r.data.error); }
      else { setPlan(r.data); toast.success("Daily plan generated"); onUsageChange?.(); }
    } catch (e) {
      const message = e.response?.data?.detail || "Could not generate. Please try again.";
      setError(message); toast.error(message);
    } finally { setLoading(false); }
  };

  const download = async () => {
    try {
      const r = await api.get("/meal-plan/download", { params: { plan_type: "daily" }, responseType: "blob" });
      downloadBlob(r.data, r.headers, "NutriGuide_daily-meal-plan.pdf");
      toast.success("Downloaded");
    } catch (e) {
      toast.error("Could not download PDF");
    }
  };

  const save = async (name) => {
    setSavingBusy(true);
    try {
      await api.post("/meal-plans/saved", { name, plan_type: "daily" });
      setSaveOpen(false);
      toast.success("Saved");
    } catch (e) {
      toast.error("Could not save");
    } finally { setSavingBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {plan && !plan.error && (
            <>
              <Button variant="outline" onClick={() => setSaveOpen(true)} className="rounded-full" data-testid="save-daily-btn">
                <BookmarkPlus className="w-4 h-4 mr-2" /> Save
              </Button>
              <Button variant="outline" onClick={download} className="rounded-full" data-testid="download-daily-btn">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </>
          )}
        </div>
        <Button onClick={generate} disabled={loading || overQuota} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="generate-daily-btn">
          <Sparkles className="w-4 h-4 mr-2" />
          {loading ? "Generating…" : plan ? "Regenerate" : "Generate"}
        </Button>
      </div>

      {overQuota && !plan && (
        <Card className="rounded-3xl border-amber-100 bg-amber-50/60 p-4 mb-4 text-sm text-amber-800" data-testid="quota-exhausted-daily">
          You've used both meal-plan generations for today. Your allowance resets at 12AM GMT — your saved plans are always available in the <b>Saved</b> tab.
        </Card>
      )}

      {error && <p role="alert" data-testid="daily-generation-error" className="mb-4 rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</p>}
      {!plan && !loading && !overQuota && (
        <Card className="rounded-3xl border-dashed border-emerald-200 p-12 text-center">
          <Sparkles className="w-10 h-10 text-emerald-300 mx-auto mb-4" />
          <p className="font-display font-semibold text-slate-700">No daily plan yet</p>
          <p className="text-sm text-slate-500 mt-2">Click Generate to create a personalized day.</p>
        </Card>
      )}

      {plan?.error && (
        <Card className="rounded-3xl border-red-100 bg-red-50/40 p-6 text-red-700 text-sm">{plan.error}</Card>
      )}

      {plan && !plan.error && (
        <div className="space-y-5">
          {MEALS.map(({ key, label, Icon, accent }, i) =>
            plan[key] ? (
              <Card key={key} className={`rounded-3xl border-${accent}-100 p-6 fade-up delay-${i + 1}`} data-testid={`meal-${key}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-2xl bg-${accent}-50 flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-6 h-6 text-${accent}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className={`text-xs text-${accent}-700 font-semibold tracking-wider uppercase`}>{label}</div>
                      <div className="flex items-center gap-2">
                        {plan[key].calories && <span className="text-xs text-slate-500">{plan[key].calories} kcal</span>}
                        <button
                          type="button"
                          onClick={() => openSaveComponent(key)}
                          className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full bg-${accent}-50 text-${accent}-700 hover:bg-${accent}-100 transition-colors`}
                          data-testid={`save-meal-${key}`}
                          title="Save this meal to your library"
                        >
                          <BookmarkPlus className="w-3 h-3" /> Save
                        </button>
                      </div>
                    </div>
                    <div className="font-display text-xl font-semibold text-slate-900">{plan[key].name}</div>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{plan[key].description}</p>
                    {plan[key].why_good && (
                      <div className="mt-3 text-xs text-emerald-800 bg-emerald-50/60 rounded-xl p-3">
                        <span className="font-semibold">Why for you:</span> {plan[key].why_good}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ) : null
          )}
          {plan.snacks?.length > 0 && (
            <Card className="rounded-3xl border-emerald-100 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Cookie className="w-5 h-5 text-emerald-600" />
                <div className="font-display font-semibold text-slate-900">Snacks</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {plan.snacks.map((s, i) => (
                  <div key={i} className="rounded-2xl bg-emerald-50/40 p-4 relative group">
                    <button
                      type="button"
                      onClick={() => openSaveComponent("snack", i)}
                      className="absolute top-2 right-2 text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                      data-testid={`save-snack-${i}`}
                      title="Save this snack"
                    >
                      <BookmarkPlus className="w-3 h-3" />
                    </button>
                    <div className="font-medium text-slate-900 text-sm pr-8">{s.name}</div>
                    {s.calories && <div className="text-xs text-slate-500 mb-1">{s.calories} kcal</div>}
                    <p className="text-xs text-slate-600 mt-1">{s.description}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {plan.tips?.length > 0 && (
            <Card className="rounded-3xl border-sky-100 bg-sky-50/30 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="w-5 h-5 text-sky-600" />
                <div className="font-display font-semibold text-slate-900">Smart tips</div>
              </div>
              <ul className="space-y-2">
                {plan.tips.map((t, i) => (
                  <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-sky-500">•</span>{t}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      <SavePlanDialog
        open={saveOpen}
        defaultName={`Daily plan · ${new Date().toLocaleDateString()}`}
        onClose={() => setSaveOpen(false)}
        onSave={save}
        busy={savingBusy}
      />
      <SaveComponentDialog
        open={!!compOpen}
        defaultName={compOpen?.defaultName || ""}
        onClose={() => setCompOpen(null)}
        onSave={saveComponent}
        busy={compBusy}
      />
    </div>
  );
}

function WeeklyPlan() {
  return (
    <section className="py-16 sm:py-20 text-center" aria-labelledby="weekly-coming-soon-title" data-testid="weekly-coming-soon">
      <CalendarRange className="w-10 h-10 text-emerald-500 mx-auto mb-5" aria-hidden="true" />
      <h2 id="weekly-coming-soon-title" className="font-display text-base md:text-lg font-semibold text-slate-900" data-testid="weekly-coming-soon-title">
        Weekly meal plans
      </h2>
      <p className="text-sm text-slate-500 mt-2" data-testid="weekly-coming-soon-message">Coming soon</p>
    </section>
  );
}

function SavedPlans() {
  const [rows, setRows] = useState([]);
  const [comps, setComps] = useState([]);
  const [favs, setFavs] = useState([]);
  const load = () => {
    api.get("/meal-plans/saved").then((r) => setRows(r.data));
    api.get("/meal-plans/saved-components").then((r) => setComps(r.data));
    api.get("/favorites").then((r) => setFavs(r.data));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/meal-plans/saved/${id}`);
    load();
  };

  const removeComp = async (id) => {
    await api.delete(`/meal-plans/saved-components/${id}`);
    load();
  };

  const applyComp = async (c) => {
    try {
      await api.post(`/meal-plans/saved-components/${c.id}/apply`);
      toast.success(`Added "${c.name}" to today's plan`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
  };

  const removeFav = async (food_id) => {
    await api.delete(`/favorites/${food_id}`);
    load();
  };

  const download = async (row) => {
    try {
      const r = await api.get(`/meal-plans/saved/${row.id}/download`, { responseType: "blob" });
      downloadBlob(r.data, r.headers, `NutriGuide_${row.name}.pdf`);
    } catch {
      toast.error("Could not download");
    }
  };

  const isEmpty = rows.length === 0 && comps.length === 0 && favs.length === 0;
  if (isEmpty) {
    return (
      <Card className="rounded-3xl border-dashed border-emerald-200 p-12 text-center">
        <Bookmark className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Nothing saved yet. Bookmark plans, individual meals or favorite foods to see them here.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {rows.length > 0 && (
        <section>
          <h3 className="font-display font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider text-emerald-700">Full plans</h3>
          <div className="space-y-3" data-testid="saved-list">
            {rows.map((r) => (
              <Card key={r.id} className="rounded-2xl border-emerald-100 p-5 bg-white flex items-center gap-4" data-testid={`saved-${r.id}`}>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Bookmark className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-slate-900 truncate">{r.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.plan_type === "weekly" ? "Weekly plan" : "Daily plan"} · saved {new Date(r.created_at).toLocaleDateString()}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => download(r)} className="rounded-full" data-testid={`saved-download-${r.id}`}>
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`saved-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4 text-slate-400" />
                </Button>
              </Card>
            ))}
          </div>
        </section>
      )}

      {comps.length > 0 && (
        <section>
          <h3 className="font-display font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider text-emerald-700">Meal library</h3>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="saved-components-list">
            {comps.map((c) => (
              <Card key={c.id} className="rounded-2xl border-emerald-100 p-4 bg-white" data-testid={`saved-component-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">{c.component}</div>
                    <div className="font-display font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.data?.name}</div>
                    {c.data?.calories && <div className="text-xs text-slate-400 mt-0.5">{c.data.calories} kcal</div>}
                    {c.data?.description && (
                      <p className="text-xs text-slate-600 mt-2 line-clamp-3">{c.data.description}</p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => applyComp(c)}
                      className="mt-3 rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`apply-component-${c.id}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add to today&apos;s plan
                    </Button>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeComp(c.id)} data-testid={`saved-component-delete-${c.id}`}>
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {favs.length > 0 && (
        <section>
          <h3 className="font-display font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider text-emerald-700">Favorite foods</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="favorites-list">
            {favs.map((f) => (
              <Card key={f.id} className="rounded-2xl border-emerald-100 p-4 bg-white flex items-center gap-3" data-testid={`favorite-${f.food_id}`}>
                <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 text-sm truncate">{f.food?.name || f.food_name}</div>
                  <div className="text-xs text-slate-500">{f.food?.category || "food"}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeFav(f.food_id)} data-testid={`favorite-remove-${f.food_id}`}>
                  <Trash2 className="w-4 h-4 text-slate-400" />
                </Button>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function GroceryList() {
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState({});

  useEffect(() => {
    api.get("/meal-plan/grocery-list")
      .then((r) => setList(r.data.grocery_list || []))
      .catch((e) => setError(e.response?.data?.detail || "No weekly plan yet"));
  }, []);

  const toggle = (k) => setChecked((c) => ({ ...c, [k]: !c[k] }));

  if (error) {
    return (
      <Card className="rounded-3xl border-dashed border-emerald-200 p-12 text-center">
        <ShoppingBasket className="w-10 h-10 text-emerald-300 mx-auto mb-4" />
        <p className="text-sm text-slate-500">{error}. Generate a weekly plan to see your grocery list.</p>
      </Card>
    );
  }
  if (!list) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="grocery-list">
      {list.length === 0 && <div className="text-slate-500 text-sm">Empty list.</div>}
      {list.map((group, i) => (
        <Card key={i} className="rounded-3xl border-emerald-100 p-6">
          <div className="font-display font-semibold text-slate-900 mb-3">{group.category}</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {(group.items || []).map((item, j) => {
              const k = `${i}-${j}`;
              const on = !!checked[k];
              return (
                <label key={j} className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer ${on ? "bg-emerald-50" : "bg-emerald-50/30"}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(k)} className="w-4 h-4 accent-emerald-600" data-testid={`grocery-item-${i}-${j}`} />
                  <span className={`text-sm ${on ? "line-through text-slate-400" : "text-slate-700"}`}>{item}</span>
                </label>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
